import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API_URL from '../config/api';
import { useAuth } from '../context/AuthContext';
import AuthSkyBackground from '../components/AuthSkyBackground';

function getHomeRoute(businessType: string | null | undefined): string {
  const bt = (businessType || '').toLowerCase();
  if (bt.includes('property') || bt.includes('landlord') || bt.includes('letting')) {
    return '/landlord/home';
  }
  return '/dashboard';
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [status, setStatus] = useState<'unknown' | 'ok' | 'down'>('unknown');
  const [totpStep, setTotpStep] = useState(false);
  const [totpToken, setTotpToken] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [pendingRole, setPendingRole] = useState<string | null>(null);

  useEffect(() => {
    const check = async () => {
      try {
        const res = await fetch(`${API_URL}/health`);
        setStatus(res.ok ? 'ok' : 'down');
      } catch {
        setStatus('down');
      }
    };
    check();
  }, []);

  const finishLogin = (data: {
    token: string;
    role?: string | null;
    business_id?: string | null;
    business_type?: string | null;
  }) => {
    auth.login(data.token, data.role ?? null, data.business_id ?? null, data.business_type ?? null);
    if (String(data.role || '').toLowerCase() === 'super_admin') {
      navigate('/admin');
    } else {
      navigate(getHomeRoute(data.business_type));
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email || !password) return setError('Email and password required');
    try {
      const res = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (res.ok) {
        if (data.requires_totp && data.totp_token) {
          setTotpStep(true);
          setTotpToken(data.totp_token);
          setPendingRole(data.role ?? null);
          return;
        }
        finishLogin(data);
      } else setError(data.message || 'Invalid credentials');
    } catch {
      setError('Network error. Backend unreachable.');
      setStatus('down');
    }
  };

  const handleVerifyTotp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!totpCode.trim()) return setError('Enter the 6-digit code from your authenticator app');
    try {
      const res = await fetch(`${API_URL}/auth/admin/verify-totp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ totp_token: totpToken, code: totpCode.trim() }),
      });
      const data = await res.json();
      if (res.ok) {
        finishLogin({ ...data, role: data.role ?? pendingRole });
      } else {
        setError(data.message || 'Invalid authentication code');
      }
    } catch {
      setError('Network error. Backend unreachable.');
    }
  };

  return (
    <div style={{ minHeight:'100vh', background:'#03040e', display:'flex', alignItems:'center', justifyContent:'center', position:'relative', overflow:'hidden', padding:'48px 20px' }}>
      <style>{`
        @keyframes floatCard { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-3px)} }
        .login-input:focus { border-color: rgba(127,119,221,.55) !important; outline: none; }
        .login-input::placeholder { color: rgba(140,130,190,.28); }
        .btn-primary:hover { opacity: .87; }
        .btn-secondary:hover { background: rgba(127,119,221,.08) !important; border-color: rgba(127,119,221,.4) !important; }
        .link-btn:hover { color: rgba(200,190,240,.85) !important; }
        @media (prefers-reduced-motion: reduce) {
          .login-card { animation: none !important; }
        }
      `}</style>

      <AuthSkyBackground />

      <div
        className="login-card"
        style={{
          position: 'relative', zIndex: 10, width: '100%', maxWidth: 400,
          background: 'rgba(8,12,28,.78)',
          border: '1px solid rgba(140,130,200,.18)',
          borderRadius: 22,
          padding: '42px 38px 38px',
          backdropFilter: 'blur(18px)',
          WebkitBackdropFilter: 'blur(18px)',
          boxShadow: '0 24px 60px rgba(0,0,0,.35), 0 0 0 1px rgba(255,255,255,.03) inset',
          animation: 'floatCard 10s ease-in-out infinite',
        }}
      >
        <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:11, marginBottom:10 }}>
          <div style={{ width:38, height:38, borderRadius:11, background:'linear-gradient(140deg,#7f77dd,#3c3489)', display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 8px 20px rgba(80,70,180,.28)' }}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="4" fill="#ede9ff"/>
              <circle cx="10" cy="3.5" r="2" fill="#ede9ff" opacity=".55"/>
              <circle cx="16" cy="13.5" r="2" fill="#ede9ff" opacity=".55"/>
              <circle cx="4" cy="13.5" r="2" fill="#ede9ff" opacity=".55"/>
            </svg>
          </div>
          <span style={{ fontSize:22, fontWeight:700, letterSpacing:.4, fontFamily:'system-ui,sans-serif', background:'linear-gradient(180deg,#ffffff 0%,#e8e5f5 55%,#b8b2d4 100%)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', textShadow:'0 0 24px rgba(220,215,255,.15)' }}>ShahoSuite</span>
        </div>

        <p style={{ textAlign:'center', fontSize:13, color:'#c9c3e2', marginBottom:8, fontFamily:'system-ui,sans-serif', letterSpacing:.2, lineHeight:1.45 }}>
          Everything your business needs, in one place
        </p>

        <div style={{ textAlign:'center', fontSize:12.5, marginBottom:24, fontFamily:'system-ui,sans-serif', fontWeight:500 }}>
          {status === 'unknown' && <span style={{ color:'rgba(200,195,230,.6)' }}>● Checking...</span>}
          {status === 'ok' && <span style={{ color:'#2fc492' }}>● All systems online</span>}
          {status === 'down' && <span style={{ color:'#e24b4a' }}>● Service unavailable</span>}
        </div>

        <form onSubmit={totpStep ? handleVerifyTotp : handleLogin}>
          {!totpStep ? (
            <>
          <div style={{ marginBottom:15 }}>
            <label style={{ display:'block', fontSize:11.5, fontWeight:600, color:'#cfc9e8', marginBottom:7, textTransform:'uppercase', letterSpacing:.9, fontFamily:'system-ui,sans-serif' }}>Email</label>
            <input
              className="login-input"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              style={{ width:'100%', padding:'12px 15px', background:'rgba(255,255,255,.04)', border:'1px solid rgba(120,110,190,.18)', borderRadius:10, color:'#ede9ff', fontSize:15, fontFamily:'system-ui,sans-serif', boxSizing:'border-box' }}
            />
          </div>

          <div style={{ marginBottom:15 }}>
            <label style={{ display:'block', fontSize:11.5, fontWeight:600, color:'#cfc9e8', marginBottom:7, textTransform:'uppercase', letterSpacing:.9, fontFamily:'system-ui,sans-serif' }}>Password</label>
            <input
              className="login-input"
              type="password"
              placeholder="Enter your password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              style={{ width:'100%', padding:'12px 15px', background:'rgba(255,255,255,.04)', border:'1px solid rgba(120,110,190,.18)', borderRadius:10, color:'#ede9ff', fontSize:15, fontFamily:'system-ui,sans-serif', boxSizing:'border-box' }}
            />
          </div>
            </>
          ) : (
            <>
              <p style={{ color: 'rgba(160,150,210,.65)', fontSize: 13, marginBottom: 16, textAlign: 'center' }}>
                Enter the 6-digit code from your authenticator app to access the platform admin.
              </p>
              <div style={{ marginBottom: 15 }}>
                <label style={{ display:'block', fontSize:11.5, fontWeight:600, color:'#cfc9e8', marginBottom:7, textTransform:'uppercase', letterSpacing:.9, fontFamily:'system-ui,sans-serif' }}>Authentication code</label>
                <input
                  className="login-input"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="000000"
                  value={totpCode}
                  onChange={e => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  style={{ width:'100%', padding:'12px 15px', background:'rgba(255,255,255,.04)', border:'1px solid rgba(120,110,190,.18)', borderRadius:10, color:'#ede9ff', fontSize:15, fontFamily:'system-ui,sans-serif', boxSizing:'border-box', letterSpacing: 6, textAlign: 'center' }}
                />
              </div>
            </>
          )}

          {error && (
            <div style={{ color:'#f09595', fontSize:13, textAlign:'center', marginBottom:12, fontFamily:'system-ui,sans-serif', background:'rgba(226,75,74,.1)', padding:'10px 14px', borderRadius:8, border:'1px solid rgba(226,75,74,.2)' }}>
              {error}
            </div>
          )}

          <button
            type="submit"
            className="btn-primary"
            style={{ width:'100%', padding:13, marginTop:6, background:'linear-gradient(135deg,#7f77dd 0%,#534ab7 100%)', border:'none', borderRadius:11, color:'#fff', fontSize:15, fontWeight:600, cursor:'pointer', fontFamily:'system-ui,sans-serif', letterSpacing:.3, transition:'opacity .2s' }}
          >
            {totpStep ? 'Verify & sign in' : 'Sign in to ShahoSuite'}
          </button>

          {totpStep && (
            <button
              type="button"
              className="link-btn"
              onClick={() => { setTotpStep(false); setTotpCode(''); setTotpToken(''); setError(''); }}
              style={{ width: '100%', marginTop: 12, background: 'none', border: 'none', fontSize: 12.5, color: 'rgba(200,194,230,.7)', cursor: 'pointer' }}
            >
              ← Back to password
            </button>
          )}

          {!totpStep && (
          <div style={{ display:'flex', justifyContent:'space-between', marginTop:16 }}>
            <span
              className="link-btn"
              onClick={() => navigate('/forgot')}
              style={{ fontSize:12.5, color:'rgba(200,194,230,.7)', fontFamily:'system-ui,sans-serif', cursor:'pointer', transition:'color .2s' }}
            >
              Forgot password?
            </span>
            <a
              href="mailto:admin@shahosuite.com?subject=ShahoSuite%20Login%20Help"
              style={{ fontSize:12.5, color:'rgba(200,194,230,.7)', fontFamily:'system-ui,sans-serif', cursor:'pointer', transition:'color .2s', textDecoration:'none' }}
            >
              Need help?
            </a>
          </div>
          )}

          {!totpStep && (
          <>
          <div style={{ height:1, background:'rgba(120,110,190,.1)', margin:'20px 0' }} />

          <button
            type="button"
            className="btn-secondary"
            onClick={() => navigate('/register')}
            style={{ width:'100%', padding:11, background:'transparent', border:'1px solid rgba(120,110,190,.25)', borderRadius:11, color:'#d6d1ec', fontSize:14, cursor:'pointer', fontFamily:'system-ui,sans-serif', transition:'all .2s' }}
          >
            Create a new account
          </button>
          </>
          )}
        </form>
      </div>
    </div>
  );
};

export default Login;
